import { skipToken } from "@reduxjs/toolkit/query";

import { ConnectProto } from "@racoon/api";
import { fromTimestamp } from "@racoon/lib";

import { useListDriverIncidentsQuery } from "~/shared/api/driver-incidents/driver-incidents.grpc";
import { jobIncidentNote, newestIncident } from "~/shared/lib/job-incident";
import {
  incidentPickFor,
  incidentToSave,
  isIncidentPickValid,
  pickToRecord,
  type CurrentIncident,
  type StagedIncident,
} from "./incident-correction";
import { enumNumber } from "./job-enums";
import { outcomeOf, type Outcome } from "./outcome-correction";

const { JobStatus } = ConnectProto;

/** The job's reason as the report reads it: saved, as staged, and what a save records. */
export const useReportIncident = (
  job: ConnectProto.Job,
  stagedOutcome: Outcome | undefined,
  picked: StagedIncident | undefined,
) => {
  const { incidents } = useListDriverIncidentsQuery(
    job.serviceProviderId
      ? { ownerOrganizationId: BigInt(job.serviceProviderId), jobIds: [BigInt(job.id)] }
      : skipToken,
    { selectFromResult: ({ data }) => ({ incidents: data?.driverIncidents }) },
  );
  // The job map hands its jobs over with enum names
  const status = enumNumber(ConnectProto.JobStatusSchema, job.status) ?? JobStatus.UNDEFINED;
  const outcome = stagedOutcome ?? outcomeOf(job.status);
  const staged = incidentPickFor(picked, outcome);
  const recorded = incidents && newestIncident(job, incidents);
  const savedNote = incidents && jobIncidentNote(job, incidents, status);
  // As the save will leave it: a job corrected to Невывоз reads «Не успели (долг)»
  const note = incidents && jobIncidentNote(job, incidents, outcome ?? status);
  // A recorded «Не успели (долг)» is a reason; a derived one is not
  const current: CurrentIncident | undefined =
    note?.kind === "incident"
      ? note
      : note?.kind === "debt" && recorded?.type === ConnectProto.DriverIncidentType.EXPIRED
        ? { type: recorded.type, explanation: note.explanation }
        : undefined;

  return {
    outcome,
    staged,
    savedNote,
    savedAt: savedNote && recorded && fromTimestamp(recorded.createdAt),
    // A cancelled job starts with no reason: a Невывоз's does not carry over
    note: outcome === JobStatus.CANCELLED ? undefined : note,
    // What a verdict press records. Until the incidents arrive the job may have a reason,
    // so no default is written
    toSave: incidentToSave(
      pickToRecord(staged, {
        becomesNotCompleted: stagedOutcome === JobStatus.NOT_COMPLETED,
        hasReason: !incidents || recorded !== undefined || note?.kind === "incident",
      }),
      current,
    ),
    // «Другое» without words waits for them
    blocksSave: staged !== undefined && !isIncidentPickValid(staged),
  };
};
